
import MissionTabs from '@/components/Mission/MissionTabs.vue';
import Vue from 'vue';
import Component from 'vue-class-component';
import MissionTabNames from '@/flightpath-components/services/missionTabNames';
import MissionMap from '@/components/Mission/MissionMap.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { SinglePathMission } from '@/flightpath-components/models/SinglePathMission';
import { ImplementOperationsModel } from '@/flightpath-components/models/implement/ImplementOperationsModel';
import { FieldOperationsModel } from '@/flightpath-components/models/FieldOperationsModel';
import { getModule } from 'vuex-module-decorators';
import MissionStore from '@/store/modules/missions';
import FieldStore from '../../store/modules/fields';
import { IntersectActionHelper } from '@/utils/IntersectActionHelper';
import { Watch } from 'vue-property-decorator';
import { ImplementKeys, Parameters, SprayerProductTypes } from '@/services/Parameters';
import MissionToolTypes from '@/services/MissionToolTypes';
import { SortHelper } from '@/services/SortHelper';
import DurationFinder from '@/utils/DurationFinder';
import GlobalStore from '@/store/modules/global';
import { Guid } from 'guid-typescript';
import { toNumber } from 'lodash';

// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'AddAndEditMissions', inject: ['container'], components: { MissionTabs, MissionMap } })
export default class AddAndEditMissions extends Vue {
  globalStore = getModule(GlobalStore, this.$store);
  tabName = MissionTabNames.AddAndEditMissions;
  loading = true;
  panel = [0];
  missions: SinglePathMission[] = [];
  availableFields: FieldOperationsModel[] = [];
  selectedMission = new SinglePathMission();
  missionStore = getModule(MissionStore, this.$store);
  public fieldStore = getModule(FieldStore, this.$store);
  missionSelected = false;
  editMode = false;
  deleteDialog = false;
  newMissionName = '';
  workAreaArea = 0;
  implements: ImplementOperationsModel[] = [];
  selectedImplement = new ImplementOperationsModel();
  vineWidth = 0;
  volume = 0;
  sprayProduct = '';
  selectedMissionIndex = -1;
  missionTypes = MissionToolTypes.missionTypes;
  selectedMissionType = '';
  toolTypes = MissionToolTypes.toolTypes;
  selectedToolType = '';
  speed = 0;
  engineSpeed = 0;
  editFormValid = false;

  windowSize = {
    x: 0,
    y: 0
  };

  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  private container!: Container;

  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.onResize();
    this.loading = true;

    await this.getAvailableMissions()
      .then(() => {
        this.loading = false;
        if (this.missionStore.createdMission != null) {
          this.selectedMissionIndex = this.missions.findIndex((x) => x.MissionID == this.missionStore.createdMission?.MissionID);
          this.selectMission(this.missionStore.createdMission);
          this.missionStore.setNewlyCreatedMission(null);
        }
      })
      .catch(() => (this.loading = true));

    await this.getImplements();
    await this.getAvailableFields();
  }

  @Watch('missionStore.workAreaAreaM2')
  onWorkAreaAreaChange(area: number) {
    this.workAreaArea = this.missionStore.workAreaHectares;
  }

  @Watch('selectedMissionType')
  async onSelectedMissionType() {
    await this.$nextTick();
    this.validate();
  }

  get sprayerProducts(): SprayerProductTypes[] {
    return Object.values(SprayerProductTypes);
  }

  validate(): boolean {
    return (this.$refs as any).form.validate();
  }

  async getAvailableMissions() {
    if (this.flightPathService == undefined) return;

    const response = await this.flightPathService.getMissionsListByCustomerId(this.globalStore.accountId);
    this.missions = SinglePathMission.toModelCollection(JSON.stringify(response.data));
    this.missions = this.missions.sort((a, b) => SortHelper.compareStrings(a.MissionName as string, b.MissionName as string));
  }

  getMissionsForField(fieldId: string): SinglePathMission[] {
    const missions = this.missions.filter((x) => x.ReferenceFieldID == fieldId);
    return missions;
  }

  async getAvailableFields() {
    if (!this.flightPathService) return;

    const response = await this.flightPathService.getAvailableFields(this.globalStore.accountId);
    this.availableFields = FieldOperationsModel.toModelCollection(JSON.stringify(response.data));
    this.availableFields = this.availableFields.sort((a, b) => SortHelper.compareStrings(a.FieldName as string, b.FieldName as string));
    this.availableFields = this.availableFields.filter((x) => this.missions.some((y) => y.ReferenceFieldID == x.FieldID));
  }

  async getImplements() {
    if (this.flightPathService == undefined) return;

    const response = await this.flightPathService.getAllImplements(this.globalStore.accountId);
    this.implements = ImplementOperationsModel.toModelCollection(JSON.stringify(response.data));
    // Add fake implements
    if (this.implements.length == 0) await this.addFakeImplements();
  }

  async addFakeImplements() {
    if (this.flightPathService == undefined) return;

    const fakeNoneString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"none","ImplementID":"00000000-0000-0000-0000-000000000001","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';
    const fakeDiscString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"disc","ImplementID":"00000000-0000-0000-0000-000000000006","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';
    const fakeBladeString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"blade","ImplementID":"00000000-0000-0000-0000-000000000007","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';
    const fakeMowerString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"mower","ImplementID":"00000000-0000-0000-0000-000000000008","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';
    const fakePloughString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"plough","ImplementID":"00000000-0000-0000-0000-000000000010","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';
    const fakeSprayerString =
      '{"ErrorNotes":"","DistanceGPSToHitchX":0.0,"DistanceGPSToHitchY":0.0,"DistanceHitchToImplementCenterX":0.0,"DistanceHitchToImplementCenterY":0.0,"DistanceGPSToImplementCenterX":0.0,"DistanceGPSToImplementCenterY":-0.83,"DistanceGPSToTractorBackY":1.0,"LeftBuffer":0.5,"RightBuffer":0.5,"GPSOnTractor":false,"ThreePointHitch":false,"CreationTime":0,"NumberOfZones":1,"ImplementName":"sprayer","ImplementID":"00000000-0000-0000-0000-000000000009","SectionMap":{},"MachineApproachDistanceMeters":"10","SwathBufferDistanceMeters":"1","WheelbaseMeters":"3","MaxSteeringAngleDegrees":"30","SectionControlConfiguration":{"ErrorNotes":"","OverlapThreshold":0.5,"Mode":"Threshold","LookAheadSecondsEntering":0.5,"LookAheadSecondsExiting":1.5,"SystemDelaySecondsEntering":0.5,"SystemDelaySecondsExiting":1.5,"DesiredOverlapMetersEntering":0,"DesiredOverlapMetersExiting":0,"MaxPopulation":1000,"MinPopulation":1,"GPSOffsetSeconds":1},"SectionList":[{"Item1":0,"Item2":0.0}],"SectionWidths":[1]}';

    const fakeNoneImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakeNoneString);
    const fakeDiscImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakeDiscString);
    const fakeBladeImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakeBladeString);
    const fakeMowerImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakeMowerString);
    const fakePloughImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakePloughString);
    const fakeSprayerImplement: ImplementOperationsModel = ImplementOperationsModel.toModel(fakeSprayerString);

    // assign new implement IDs so that existing implements don't get overwritten with a new account id
    fakeNoneImplement.ImplementID = Guid.create().toString();
    fakeDiscImplement.ImplementID = Guid.create().toString();
    fakeBladeImplement.ImplementID = Guid.create().toString();
    fakeMowerImplement.ImplementID = Guid.create().toString();
    fakePloughImplement.ImplementID = Guid.create().toString();
    fakeSprayerImplement.ImplementID = Guid.create().toString();

    await this.flightPathService.addImplement(fakeNoneImplement, this.globalStore.accountId);
    await this.flightPathService.addImplement(fakeDiscImplement, this.globalStore.accountId);
    await this.flightPathService.addImplement(fakeBladeImplement, this.globalStore.accountId);
    await this.flightPathService.addImplement(fakeMowerImplement, this.globalStore.accountId);
    await this.flightPathService.addImplement(fakePloughImplement, this.globalStore.accountId);
    await this.flightPathService.addImplement(fakeSprayerImplement, this.globalStore.accountId);
  }

  async selectMission(mission: SinglePathMission) {
    if (this.editMode) return;
    if (!this.flightPathService) return;
    this.loading = true;
    try {
      const response = await this.flightPathService.getMissionByMissionId(mission.MissionID, this.globalStore.accountId);
      const singlePathMission = SinglePathMission.toModel(JSON.stringify(response.data));
      const deselect = mission != null && this.missionStore.getEditedMission?.MissionID == singlePathMission.MissionID;
      this.missionStore.setEditedMission(deselect ? null : singlePathMission);
      this.selectedMission = deselect ? new SinglePathMission() : singlePathMission;
      this.missionSelected = !deselect;
      this.newMissionName = deselect ? '' : singlePathMission.MissionName;
      this.selectedImplement = deselect ? new ImplementOperationsModel() : this.findImplement();
      this.speed = this.selectedMission.TargetSpeedKph;
      deselect ? this.clearApplicationParameters() : this.getApplicationParameters();
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  findImplement(): ImplementOperationsModel {
    const implement = this.implements.find((x) => x.ImplementID == this.selectedMission.ImplementCode);
    return implement == undefined ? new ImplementOperationsModel() : implement;
  }

  async saveMission() {
    if (this.flightPathService == undefined) return;

    this.editMode = false;
    this.selectedMission.MissionName = this.newMissionName;
    this.selectedMission.ImplementCode = this.selectedImplement.ImplementID;
    this.selectedMission.TargetSpeedKph = this.speed;
    this.setApplicationParameters();
    const missionWithIntersects = IntersectActionHelper.modifyMissionIntersects(this.selectedMission, true);
    await this.flightPathService.insertOrUpdateMission(missionWithIntersects);
  }

  editMission() {
    this.editMode = true;
  }

  cancelEdit() {
    this.editMode = false;
    this.newMissionName = this.selectedMission.MissionName;
    this.selectedImplement = this.findImplement();
    this.getApplicationParameters();
  }

  async deleteMission() {
    this.deleteDialog = false;
    if (this.flightPathService == undefined) return;

    await this.flightPathService.deleteMissionByMissionId(this.selectedMission.MissionID, this.globalStore.accountId);
    this.selectMission(this.selectedMission);
    await this.getAvailableMissions();
  }

  getApplicationParameters() {
    if (this.selectedMission.ApplicationParameters == undefined) return;

    const params = this.selectedMission.getApplicationParameters();
    this.vineWidth = params.get(Parameters.vineWidth) != undefined ? toNumber(params.get(Parameters.vineWidth) as string) : 0;
    this.volume = params.get(Parameters.volume) != undefined ? toNumber(params.get(Parameters.volume) as string) : 0;
    this.sprayProduct = params.get(Parameters.sprayProduct) != undefined ? (params.get(Parameters.sprayProduct) as string) : '';
    const implementKey = params.get(Parameters.implementType);
    this.selectedMissionType = implementKey == ImplementKeys.sprayer ? ImplementKeys.sprayer : 'tillage';
    this.selectedToolType = implementKey == ImplementKeys.sprayer ? ImplementKeys.sprayer : implementKey ?? '';
    this.engineSpeed = toNumber(params.get(Parameters.engineSpeed));
  }

  setApplicationParameters() {
    const map = new Map();
    map.set(Parameters.implementType, this.implementKey);
    if (this.selectedMissionType == ImplementKeys.sprayer) {
      map.set(Parameters.vineWidth, this.vineWidth);
      map.set(Parameters.volume, this.volume);
      map.set(Parameters.sprayProduct, this.sprayProduct);
    }
    map.set(Parameters.engineSpeed, this.engineSpeed);
    this.selectedMission = this.selectedMission.setApplicationParameters(map);
    console.log(map);
  }

  get implementKey() {
    if (this.selectedMissionType == ImplementKeys.sprayer) {
      return ImplementKeys.sprayer;
    } else {
      return this.selectedToolType;
    }
  }

  clearApplicationParameters() {
    this.vineWidth = 0;
    this.volume = 0;
    this.sprayProduct = '';
  }

  get toolTypeLabel() {
    if (this.selectedMission.MissionID == Guid.EMPTY.toString()) return '';
    return this.toolTypes.find((x) => x.value == this.selectedMission.getApplicationParameters().get(Parameters.implementType))?.text ?? '';
  }

  get missionType() {
    if (this.selectedMission.MissionID == Guid.EMPTY.toString()) return '';
    if (this.selectedMission.getApplicationParameters().get(Parameters.implementType) == ImplementKeys.sprayer) {
      return this.$t('views.missions.spraying_mission');
    } else {
      return this.$t('views.missions.tillage');
    }
  }

  get availableToolTypes() {
    if (this.selectedMission.MissionID == Guid.EMPTY.toString()) return [];
    // only get tool types (implementts) that are currently in the database
    return this.toolTypes.filter((x) => this.implements.findIndex((implement) => implement.ImplementName == x.value && x.value != 'weeder') > -1);
  }

  get estimatedDurationHours() {
    if (this.speed == 0) return '0 h 0 min';

    return DurationFinder.findEstimatedMissionDurationHoursMinutes(
      this.selectedMission.Path.map((x) => new window.google.maps.LatLng(x.Lat, x.Long)),
      this.speed
    );
  }
}
